import { useRef, useState, useEffect } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import ChartContainer from './ChartContainer';
import { useTheme } from '../../hooks/useTheme';
import {
  createChartAnimations,
  createHoverEffects,
  createEnhancedTooltips,
  createInteractionEffects,
  ChartInteractionHandler
} from '../../utils/chartUtils';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

function BudgetUtilizationChart({ data, loading = false, onDepartmentClick }) {
  const chartRef = useRef();
  const [currentData, setCurrentData] = useState(null);
  const [drillDownLevel, setDrillDownLevel] = useState(0);
  const [interactionHandler] = useState(() => new ChartInteractionHandler(chartRef, {
    enableDrillDown: true,
    enableDetailedViews: true,
    enableContextMenu: true
  }));
  const [isAnimating, setIsAnimating] = useState(false);
  const [highlightedElements, setHighlightedElements] = useState([]);
  const { theme } = useTheme();

  // Force chart update when theme changes
  useEffect(() => {
    if (chartRef.current) {
      const chart = chartRef.current;
      // Update chart colors based on current theme
      chart.options.plugins.legend.labels.color = getComputedStyle(document.documentElement).getPropertyValue('--text').trim();
      chart.options.scales.x.ticks.color = getComputedStyle(document.documentElement).getPropertyValue('--muted').trim();
      chart.options.scales.y.ticks.color = getComputedStyle(document.documentElement).getPropertyValue('--muted').trim();
      chart.options.scales.x.grid.color = getComputedStyle(document.documentElement).getPropertyValue('--border').trim();
      chart.options.scales.y.grid.color = getComputedStyle(document.documentElement).getPropertyValue('--border').trim();
      chart.update('none');
    }
  }, [theme]);

  // Use real data or empty array if no data provided
  const chartData = currentData || data || [];

  // Initialize interaction handlers
  useEffect(() => {
    if (interactionHandler) {
      // Register detailed view callback
      interactionHandler.registerDetailView('default', ({ elementIndex, chartData }) => {
        const element = chartData[elementIndex];
        if (element) {
          // Show detailed view modal or panel
          console.log('Detailed view for:', element);
          return {
            type: 'detailed-view',
            data: element,
            elementIndex
          };
        }
        return null;
      });

      // Register context menu callback
      interactionHandler.registerContextMenu('default', ({ elementIndex, chartData, position }) => {
        console.log('Context menu for element:', elementIndex, 'at position:', position);
        // Here you could show a context menu with options like:
        // - View details
        // - Export this data
        // - Compare with others
        // - Set alert threshold
      });
    }
  }, [interactionHandler]);

  // Handle drill-down functionality with animations
  const handleDrillDown = (elementIndex, datasetIndex, data) => {
    if (drillDownLevel === 0 && data[elementIndex]?.categories) {
      setIsAnimating(true);

      const department = data[elementIndex];
      const categoryData = department.categories.map(cat => ({
        department: cat.name,
        budget: cat.budget,
        spent: cat.spent,
        utilization: Math.round((cat.spent / cat.budget) * 100)
      }));

      // Animate the transition
      setTimeout(() => {
        setCurrentData(categoryData);
        setDrillDownLevel(1);
        setIsAnimating(false);
      }, 300);

      if (onDepartmentClick) {
        onDepartmentClick(department);
      }

      return categoryData;
    }
    return null;
  };

  // Handle advanced click interactions
  const handleAdvancedClick = (event, elements) => {
    if (elements.length > 0) {
      const elementIndex = elements[0].index;
      const datasetIndex = elements[0].datasetIndex;

      // Use the interaction handler for advanced click processing
      const result = interactionHandler.handleAdvancedClick(
        event,
        elements,
        chartData,
        { onDrillDown: handleDrillDown }
      );

      if (result) {
        // Handle the result based on interaction type
        console.log('Advanced click result:', result);
      }
    }
  };

  // Handle element highlighting
  const handleElementHighlight = (elementIndices) => {
    setHighlightedElements(elementIndices);

    // Trigger highlight animation
    if (chartRef.current) {
      const chart = chartRef.current;
      chart.update('none');
    }
  };

  const handleReset = (resetData) => {
    if (resetData) {
      setCurrentData(resetData);
    } else {
      setCurrentData(null);
      setDrillDownLevel(0);
    }
  };

  // Enhanced options with animations and interactions
  const options = {
    responsive: true,
    maintainAspectRatio: false,
    ...createChartAnimations(isAnimating ? 'drillDown' : 'entry'),
    ...createHoverEffects({
      enableGlow: true,
      enableScale: true,
      glowColor: 'rgba(59, 130, 246, 0.4)',
      scaleIntensity: 1.05
    }),
    ...createInteractionEffects({
      enableRipple: true,
      enableParticles: false
    }),
    ...createEnhancedTooltips({
      label: function (context) {
        const value = context.parsed.y;
        if (context.datasetIndex === 0) {
          return `Budget: ₹${value.toLocaleString('en-IN')}`;
        } else {
          return `Spent: ₹${value.toLocaleString('en-IN')}`;
        }
      },
      afterLabel: function (context) {
        if (context.datasetIndex === 1) {
          const dataIndex = context.dataIndex;
          const utilization = chartData[dataIndex]?.utilization || 0;
          return `Utilization: ${utilization}%`;
        }
        return '';
      },
      footer: function () {
        if (drillDownLevel === 0) {
          return 'Click to view category breakdown';
        }
        return '';
      }
    }),
    plugins: {
      legend: {
        position: 'top',
        labels: {
          color: theme === 'dark' ? '#f8fafc' : '#1f2937',
          font: {
            size: 12
          }
        }
      },
      title: {
        display: false
      }
    },
    scales: {
      x: {
        ticks: {
          color: theme === 'dark' ? '#94a3b8' : '#6b7280'
        },
        grid: {
          color: theme === 'dark' ? '#334155' : '#e2e8f0'
        }
      },
      y: {
        beginAtZero: true,
        ticks: {
          color: theme === 'dark' ? '#94a3b8' : '#6b7280',
          callback: function (value) {
            return '₹' + (value / 1000) + 'K';
          }
        },
        grid: {
          color: theme === 'dark' ? '#334155' : '#e2e8f0'
        }
      }
    },
    onClick: handleAdvancedClick,
    onDoubleClick: (event, elements) => {
      // Double-click for quick detailed view
      if (elements.length > 0) {
        const elementIndex = elements[0].index;
        interactionHandler.showDetailedView(elementIndex, 0, chartData);
      }
    },
    // Context menu will be handled separately to avoid circular reference
  };

  const chartDataConfig = {
    labels: chartData.map(item => item.department),
    datasets: [
      {
        label: 'Budget',
        data: chartData.map(item => item.budget),
        backgroundColor: 'rgba(59, 130, 246, 0.3)',
        borderColor: 'rgba(59, 130, 246, 1)',
        borderWidth: 2,
        borderRadius: 4,
        borderSkipped: false,
      },
      {
        label: 'Spent',
        data: chartData.map(item => item.spent),
        backgroundColor: chartData.map((item, index) => {
          const utilization = item.utilization || 0;
          const isHighlighted = highlightedElements.includes(index);
          const alpha = isHighlighted ? 0.9 : 0.7;

          if (utilization >= 90) return `rgba(239, 68, 68, ${alpha})`; // Red for critical
          if (utilization >= 75) return `rgba(245, 158, 11, ${alpha})`; // Yellow for warning
          return `rgba(34, 197, 94, ${alpha})`; // Green for healthy
        }),
        borderColor: chartData.map((item) => {
          const utilization = item.utilization || 0;

          if (utilization >= 90) return 'rgba(239, 68, 68, 1)';
          if (utilization >= 75) return 'rgba(245, 158, 11, 1)';
          return 'rgba(34, 197, 94, 1)';
        }),
        borderWidth: 2,
        borderRadius: 4,
        borderSkipped: false,
      }
    ]
  };

  const getChartTitle = () => {
    if (drillDownLevel === 0) {
      return 'Budget Utilization by Department';
    } else {
      return 'Category Breakdown';
    }
  };

  const getChartSubtitle = () => {
    if (drillDownLevel === 0) {
      return 'Click on a department to view category details';
    } else {
      return 'Detailed view of budget categories';
    }
  };

  if (loading) {
    return (
      <ChartContainer
        title={getChartTitle()}
        subtitle={getChartSubtitle()}
        loading={true}
        chartRef={chartRef}
        chartData={chartDataConfig}
      >
        <div className="h-64 flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderColor: 'var(--primary)' }}></div>
        </div>
      </ChartContainer>
    );
  }

  return (
    <ChartContainer
      title={getChartTitle()}
      subtitle={getChartSubtitle()}
      onDrillDown={handleDrillDown}
      onReset={handleReset}
      chartRef={chartRef}
      chartData={chartDataConfig}
      showDrillDownNavigation={true}
      enableAnimations={true}
    >
      <div className="h-64">
        <Bar ref={chartRef} data={chartDataConfig} options={options} />
      </div>
    </ChartContainer>
  );
}

export default BudgetUtilizationChart;